import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { IoChevronBack, IoCallOutline } from 'react-icons/io5';
import { TERMS_TYPES, getTerm } from '../../services/termsService';
import { useAuth } from '../../context/AuthContext';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`;
};

const TermsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { type } = useParams();
  const { userData } = useAuth();

  const hasAppHistory = location.key !== 'default';

  const getFallbackPath = () => {
    const role = userData?.role;
    if (role === 'realtor') return '/realtor/home';
    if (role === 'partner') return '/partner/dashboard';
      if (role === 'admin') return '/admin';
      if (role === 'staff') return '/admin/mail/inbox';
    return '/signup';
  };

  const handleBack = () => {
    if (hasAppHistory) navigate(-1);
    else navigate(getFallbackPath(), { replace: true });
  };

  const validType = TERMS_TYPES.some((t) => t.key === type) ? type : 'service';

  const [term, setTerm] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    (async () => {
      try {
        const data = await getTerm(validType);
        if (mounted) setTerm(data);
      } catch {
        if (mounted) setTerm(null);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [validType]);

  // 본문을 섹션별로 분리 ("제N조" 또는 "[필수/선택] 제목" 기준)
  const parseSections = (content) => {
    if (!content) return [];
    const parts = content.split(/(?=제\s*\d+조|\[(?:필수|선택)\])/);
    return parts.map((p) => p.trim()).filter(Boolean);
  };

  const sections = term?.content ? parseSections(term.content) : [];

  return (
    <Page>
      <Header>
        <HeaderInner>
          <BackBtn type="button" onClick={handleBack} aria-label="뒤로가기">
            <IoChevronBack size={22} />
          </BackBtn>
          <HeaderTitle>{term?.title || '약관'}</HeaderTitle>
        </HeaderInner>
      </Header>

      <Content>
        {loading ? (
          <LoadingText>불러오는 중...</LoadingText>
        ) : !term ? (
          <LoadingText>등록된 내용이 없습니다.</LoadingText>
        ) : (
          <>
            <DocTitle>{term.title}</DocTitle>
            <DocDate>시행일 : {formatDate(term.effective_date)}</DocDate>
            <ArticleCard>
              {sections.length > 0 ? (
                sections.map((section, i) => (
                  <Article key={i} $last={i === sections.length - 1}>
                    <ArticleText>{section}</ArticleText>
                  </Article>
                ))
              ) : (
                <ArticleText>{term.content}</ArticleText>
              )}
            </ArticleCard>
          </>
        )}
      </Content>

      <FooterWrap>
        <FooterInner>
          <IoCallOutline size={16} />
          <FooterText>문의 : 070-7666-9413 | ieum.desk@gmail.com</FooterText>
        </FooterInner>
      </FooterWrap>
    </Page>
  );
};

export default TermsPage;

// ───── 스타일 ─────
const Page = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.borderLight};
  display: flex;
  flex-direction: column;

  @media (min-width: 1024px) {
    background: #2f3542;
    align-items: center;
    padding: 40px 0;
  }
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  background: ${({ theme }) => theme.colors.bgWhite};
  z-index: 10;

  @media (min-width: 1024px) {
    position: static;
    width: 100%;
    max-width: 760px;
    background: #fff;
    border-radius: 16px 16px 0 0;
  }
`;

const HeaderInner = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.realtor.contentMaxWidth};
  margin: 0 auto;
  height: 52px;
  display: flex;
  align-items: center;
  padding: 0 ${({ theme }) => theme.spacing.md};

  @media (min-width: 1024px) {
    max-width: 760px;
    height: 60px;
    padding: 0 32px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  }
`;

const BackBtn = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: 0;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
`;

const HeaderTitle = styled.h1`
  font-size: 17px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  margin: 0 0 0 ${({ theme }) => theme.spacing.xs};
  color: ${({ theme }) => theme.colors.text};
`;

const Content = styled.main`
  flex: 1;
  width: 100%;
  max-width: ${({ theme }) => theme.realtor.contentMaxWidth};
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.realtor.pagePadding} ${({ theme }) => theme.spacing.xxl};

  @media (min-width: 1024px) {
    flex: none;
    max-width: 760px;
    background: #fff;
    border-radius: 0 0 16px 16px;
    padding: 40px 48px 60px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  }
`;

const DocTitle = styled.h2`
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const DocDate = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const ArticleCard = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  overflow: hidden;
`;

const Article = styled.div`
  padding: ${({ theme }) => theme.spacing.base} ${({ theme }) => theme.spacing.lg};
  ${({ $last, theme }) => !$last && `border-bottom: 1px solid ${theme.colors.borderLight};`}
`;

const ArticleText = styled.div`
  white-space: pre-wrap;
  font-size: ${({ theme }) => theme.fontSize.base};
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.text};
`;

const LoadingText = styled.div`
  padding: 40px 0;
  text-align: center;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const FooterWrap = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.realtor.contentMaxWidth};
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.base} ${({ theme }) => theme.realtor.pagePadding};
`;

const FooterInner = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.primary};
`;

const FooterText = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.primary};
`;
